import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from "reactstrap";
import {useGetPublicCompanyDetailQuery} from "../../store/services/publicApi";
import {MiniInfoCard} from "../../components/Public/Shared";

const CompanyDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data: company, isLoading: isCompanyLoading, error: companyError } = useGetPublicCompanyDetailQuery(id as string);

    if (isCompanyLoading) {
        return (
            <div className="text-center py-9 mt-9">
                <Spinner color="info" />
                <p className="mt-3 text-muted">Loading company profile...</p>
            </div>
        );
    }

    if (companyError || !company) {
        return (
            <Container className="py-9 mt-9 text-center">
                <Alert color="danger">
                    {companyError ? "Error loading company details. Please try again." : "Company not found."}
                </Alert>
                <Button color="info" onClick={() => navigate("/companies")}>Back to Companies</Button>
            </Container>
        );
    }

    return (
        <div className="public-company-detail-page">
            <section className="section section-lg position-relative overflow-hidden"
                style={{
                    paddingTop: '180px',
                    paddingBottom: '120px',
                    background: 'linear-gradient(180deg, #065C53 0%, #1a7a6e 60%, #2a8f82 100%)'
                }}>
                <Container className="position-relative zindex-100">
                    <Row className="align-items-center">
                        <Col lg="2" className="mb-4 mb-lg-0 text-center text-lg-left">
                            <div className="bg-white rounded-circle shadow-lg p-3 d-inline-block border" style={{ width: '150px', height: '150px', overflow: 'hidden' }}>
                                <img
                                    src={company.logo}
                                    alt={company.name}
                                    className="w-100 h-100"
                                    style={{ objectFit: 'contain' }}
                                />
                            </div>
                        </Col>
                        <Col lg="10">
                            <h1 className="display-3 text-white font-weight-bold mb-3">{company.name}</h1>
                            <div className="d-flex flex-wrap align-items-center text-white opacity-8 h5 font-weight-light">
                                <span className="mr-4 mb-2"><i className="ni ni-pin-3 mr-2" />{company.location}</span>
                                {company.website && (
                                    <span className="mr-4 mb-2">
                                        <i className="ni ni-world mr-2" />
                                        <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-white">Website</a>
                                    </span>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            <section className="section bg-secondary-lighter py-6 mt--5">
                <Container>
                    <Row>
                        <Col lg="8">
                            <Card className="border-0 shadow-lg mb-4">
                                <CardBody className="p-5">
                                    <h4 className="h4 font-weight-bold mb-4 border-bottom pb-3 text-primary">About {company.name}</h4>
                                    <div className="company-description text-dark lead" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
                                        {company.description || "Information about this company is coming soon."}
                                    </div>
                                </CardBody>
                            </Card>

                            <h3 className="h3 font-weight-bold mb-4 mt-5 text-primary">Latest Open Opportunities</h3>
                            <Row>
                                {company.jobs.length > 0 ? (
                                    company.jobs.map((job: any) => (
                                        <Col key={job.id} md="12" className="mb-3">
                                            <Card className="border-0 shadow-sm card-lift--hover">
                                                <CardBody className="p-4">
                                                    <Row className="align-items-center">
                                                        <Col md="8">
                                                            <h5 className="h5 font-weight-bold mb-1 text-dark">{job.title}</h5>
                                                            <div className="d-flex align-items-center text-muted text-sm">
                                                                <span className="mr-3"><i className="ni ni-pin-3 mr-1" />{job.location}</span>
                                                                <span className="mr-3"><i className="ni ni-briefcase-24 mr-1" />{job.employmentType}</span>
                                                            </div>
                                                        </Col>
                                                        <Col md="4" className="text-md-right mt-3 mt-md-0">
                                                            <Button color="primary" size="sm" className="rounded-pill px-4 shadow-sm" onClick={() => navigate(`/job/${job.id}`)}>
                                                                View Details
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col>
                                        <Card className="border-0 shadow-sm text-center py-5">
                                            <p className="text-muted mb-0">No open positions at the moment. Check back later!</p>
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        </Col>

                        <Col lg="4">
                            <Card className="border-0 shadow-lg position-sticky" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h5 className="h4 font-weight-bold mb-4 text-primary">Company Info</h5>

                                    <div className="mb-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                <i className="ni ni-square-pin" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Location</small>
                                                <span className="font-weight-bold text-dark">{company.location}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center mb-3">
                                            <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                <i className="ni ni-email-83" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Contact Email</small>
                                                <span className="font-weight-bold text-dark">{company?.email || "--"}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center mb-3">
                                            <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                <i className="ni ni-mobile-button" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Phone</small>
                                                <span className="font-weight-bold text-dark">{company.phone || "--"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <hr />
                                </CardBody>
                            </Card>


                            <MiniInfoCard
                                title={"Work with Us?"}
                                subtitle={"Interested in joining our team and making a difference in the energy sector?"}
                                action={{
                                    label: "Browse All Jobs",
                                    destination: "/jobs"
                                }}
                            />
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default CompanyDetail;
